<template>
    <div :class="{ 'live-mode-menubar': isLiveMode }" class="menubar-wrapper">
        <MenuBar :class="{ 'live-mode': isLiveMode }">
            <template #start>
                <button
                    v-if="!shouldHideSidebar"
                    type="button"
                    @click="toggleSideBar"
                    @mouseenter="isHovering = ui.showSidebar ? false : true"
                    :class="{ 'live-mode-button': isLiveMode }"
                >
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path
                            :fill="'var(--text-color)'"
                            fill-rule="evenodd"
                            d="M10 7h8a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-8V7ZM9 7H6a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h3V7ZM4 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8Z"
                            clip-rule="evenodd"
                        ></path>
                    </svg>
                </button>
                <RouterLink
                    id="logo-route"
                    :to="
                        stek && organization
                            ? isLiveMode
                                ? {
                                      name: 'livePage',
                                      params: {
                                          orgName: organization.name,
                                          stekName: stek.name,
                                      },
                                  }
                                : {
                                      name: 'stekPage',
                                      params: {
                                          orgName: organization.name,
                                          stekName: stek.name,
                                      },
                                  }
                            : organization
                              ? {
                                    name: 'organizationPage',
                                    params: {
                                        orgName: organization.name,
                                    },
                                }
                              : '/'
                    "
                >
                    <!-- Stek -->
                    <img
                        v-if="
                            stekHasLogoSet &&
                            logoDataUrl &&
                            logoDataUrl.length > 0
                        "
                        :src="logoDataUrl"
                        class="stek-logo"
                        alt="Stek Logo"
                        id="logo"
                    />
                    <h1 v-else-if="!stekHasLogoSet && stek?.name">
                        {{ stek.name }}
                        <span v-if="isLiveMode" class="live-badge">LIVE</span>
                        <span v-if="activeRelease" class="release-badge">{{
                            activeRelease
                        }}</span>
                    </h1>
                    <MyStekzLogo id="logo" v-else-if="!organization" />
                    <h1 v-else-if="organization && !stek">
                        {{ organization.name }}
                    </h1>
                </RouterLink>
                <Breadcrumb v-if="showBreadcrumbs" />
            </template>
            <template #end>
                <div class="toolbar-buttons-container">
                    <OmniBox />
                    <ActivityBar />
                    <SyncStatus />
                    <ReleaseSelector v-if="stek" />
                    <EditViewButton
                        v-if="
                            isViewingLatestVersion &&
                            stek &&
                            !isLiveMode &&
                            !activeRelease
                        "
                    />
                    <SettingsMenu />
                </div>
            </template>
        </MenuBar>
    </div>

    <div v-if="activeRelease && !isLiveMode" class="release-viewing-banner">
        <span class="release-viewing-banner__message">
            <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
            >
                <path
                    fill-rule="evenodd"
                    d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3C17.25 3.85 14.9 1.5 12 1.5zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5z"
                    clip-rule="evenodd"
                />
            </svg>
            Viewing release <strong>{{ activeRelease }}</strong> — read-only
        </span>
        <button
            class="release-viewing-banner__action"
            @click="clearActiveRelease()"
        >
            Back to working copy
        </button>
    </div>

    <div
        class="page-with-sidebar"
        :class="{
            'collapsed': ui.showSidebar === false ? true : false,
            'no-sidebar-mode': shouldHideSidebar,
        }"
    >
        <div
            ref="sidebar"
            id="sidebar"
            :class="{ 'hovering': isHovering }"
            v-if="!shouldHideSidebar"
        >
            <NavSideBar />
            <div
                :class="{
                    'resize-handle': true,
                    'resizing': isDragging,
                }"
                @mousedown.prevent="startResize"
            />
        </div>
        <div
            class="main-container"
            :class="{ 'show-history-sidebar': showHistory }"
        >
            <div class="page">
                <Transition name="page-enter" mode="out-in">
                    <div :key="route.fullPath" class="page-view-wrapper">
                        <RouterView />
                    </div>
                </Transition>
            </div>
            <div v-if="showHistory" class="history-sidebar">
                <ArtefactHistory />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useMouse } from "@vueuse/core";

import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { type RouteRecordName, useRoute } from "vue-router";

import ActivityBar from "@/components/ActivityBar.vue";
import ArtefactHistory from "@/components/ArtefactHistory.vue";
import Breadcrumb from "@/components/Breadcrumb.vue";
import EditViewButton from "@/components/EditViewButton.vue";
import NavSideBar from "@/components/NavSideBar.vue";
import OmniBox from "@/components/OmniBox.vue";
import ReleaseSelector from "@/components/ReleaseSelector.vue";
import SyncStatus from "@/components/SyncStatus.vue";
import { useEditEntityDialog } from "@/composables/edit-entity-dialog";
import { useEditRoleDialog } from "@/composables/edit-role-dialog";
import { useOrganizationManager } from "@/composables/organization-manager";
import { usePolicyAcceptance } from "@/composables/policy-acceptance";
import { useStekManager } from "@/composables/stek-manager";
import {
    AuthStatus,
    useAuthenticationStore,
} from "@/stores/authentication-store";
import { useHistoryManager } from "@/stores/history";
import { useReleaseManager, useReleaseStore } from "@/stores/release-store";
import { useUIStore } from "@/stores/ui-store";

import MyStekzLogo from "../assets/mystekz-logo.svg?component";
import SettingsMenu from "./menus/SettingsMenu.vue";

const route = useRoute();
const ui = useUIStore();
const { organization } = useOrganizationManager();
const { stek, logoDataUrl } = useStekManager();
const authStore = useAuthenticationStore();
const { checkAndShowPolicyDialog } = usePolicyAcceptance();

// IMPORTANT: backend can include a "logo artefact shell" even when the stek has no logo configured.
// Treat the logo as configured only if it has a preview or fetch links.
const stekHasLogoSet = computed(() => {
    const logo = stek.value?.logo;
    if (!logo) return false;
    const hasPreview = Boolean(logo.preview);
    const hasLinks = Boolean(logo.links && Object.keys(logo.links).length > 0);
    return hasPreview || hasLinks;
});

const { registerHotKey: registerRoleHotKey } = useEditRoleDialog();
const { registerHotKey: registerEntityHotKey } = useEditEntityDialog();

registerRoleHotKey();
registerEntityHotKey();

// Check for policy acceptance when user is authenticated - only on main app pages
watch(
    [() => authStore.authStatus, () => route.name],
    async ([newStatus, routeName]) => {
        if (newStatus === AuthStatus.SIGNED_IN) {
            // Only show policy dialog on main app pages, not on auth/public pages
            const mainAppRoutes: RouteRecordName[] = [
                "home",
                "organizationPage",
                "organizationHome",
                "stekPage",
                "domainPage",
                "productPage",
                "processPage",
                "profile",
                "settings",
                "application",
            ];

            if (routeName && mainAppRoutes.includes(routeName)) {
                // Small delay to ensure user data is fully loaded
                await nextTick();
                setTimeout(async () => {
                    try {
                        await checkAndShowPolicyDialog();
                    } catch (error) {
                        console.error(
                            "Error checking policy acceptance:",
                            error
                        );
                    }
                }, 500);
            }
        }
    },
    { immediate: true }
);

// Note, avoid importing 'showHistory' from the history manager. Somehow
// it will cause the route in the computed artefact in the history manager
// store to be undefined and unreactive.
const showHistory = computed(
    () => "version" in route.params && route.params.version
);

const { isViewingLatestVersion } = useHistoryManager();
const { activeRelease } = useReleaseManager();
const { clearActiveRelease } = useReleaseStore();

const toggleSideBar = () => {
    ui.toggleSideBar();
};

const showBreadcrumbs = computed(() => "breadcrumbs" in route.meta);

const shouldHideSidebar = computed(() => {
    return route.meta.hideSidebar === true;
});

const isLiveMode = computed(() => {
    return route.matched.find((m) => m.meta.mode)?.meta.mode === "live";
});

// Live mode styling is handled through CSS variables/classes to avoid inconsistencies.
// JS only toggles a single class on <body>.
watch(
    isLiveMode,
    (isLive) => {
        nextTick(() => {
            document.body.classList.toggle("live-mode-active", isLive);
        });
    },
    { immediate: true }
);

// Toggle body class so global CSS can extend padding-top when the release banner is visible.
watch(
    [activeRelease, isLiveMode],
    ([release, live]) => {
        nextTick(() => {
            document.body.classList.toggle(
                "release-viewing-active",
                Boolean(release) && !live
            );
        });
    },
    { immediate: true }
);

const sidebar = useTemplateRef<HTMLElement>("sidebar");

const isHovering = ref(false);
const isDragging = ref(false);
const { x } = useMouse();

const startResize = () => {
    isDragging.value = true;
    document.addEventListener("mousemove", handleResize);
    document.addEventListener("mouseup", stopResize);
};

const handleResize = (e: MouseEvent) => {
    if (isDragging.value && sidebar.value) {
        const newWidth = e.pageX;
        if (newWidth >= 120 && newWidth <= 500) {
            sidebar.value.style.width = `${newWidth}px`;
            sidebar.value.style.flexBasis = `${newWidth}px`;
            ui.sidebarWidth = newWidth;
        }
    }
};

const stopResize = () => {
    isDragging.value = false;
    document.removeEventListener("mousemove", handleResize);
    document.removeEventListener("mouseup", stopResize);
};

watch(x, () => {
    // if the sidebar is collapsed, make it visible (but still collapsed)
    if (x.value < 5 && !ui.showSidebar) {
        isHovering.value = true;
    }
    // Turn hovering off when mouse x leaves sidebar
    if (
        !isDragging.value &&
        isHovering.value &&
        x.value > ui.sidebarWidth + 10
    ) {
        isHovering.value = false;
    }
});

onMounted(() => {
    updateSidebarWidth();
});

// Cleanup on unmount - no longer needed

// Watch for changes to the shouldHideSidebar value
watch(
    [shouldHideSidebar, sidebar],
    () => {
        if (!shouldHideSidebar.value && sidebar.value) {
            // Use a nextTick to ensure the sidebar element is in the DOM
            nextTick(() => {
                updateSidebarWidth();
            });
        }
    },
    { immediate: true }
);

const updateSidebarWidth = () => {
    if (sidebar.value) {
        sidebar.value.style.width = `${ui.sidebarWidth}px`;
        sidebar.value.style.flexBasis = `${ui.sidebarWidth}px`;
    }
};
</script>

<style scoped lang="scss">
.menubar-wrapper {
    position: fixed;
    width: 100%;
    z-index: 1000;
    top: 0;
    left: 0;
}

.release-viewing-banner {
    position: fixed;
    top: var(--menu-bar-height);
    left: 0;
    width: 100%;
    z-index: 999;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    height: var(--release-banner-height, 36px);
    padding: 0 1rem;
    background: #fef3c7;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
    color: #78350f;
    font-size: 0.85rem;

    &__message {
        display: flex;
        align-items: center;
        gap: 0.4rem;
    }

    &__action {
        background: none;
        border: 1px solid #b45309;
        border-radius: 4px;
        color: #78350f;
        cursor: pointer;
        font-size: 0.8rem;
        padding: 0.15em 0.6em;

        &:hover {
            background: #fde68a;
        }
    }
}

.p-menubar {
    position: fixed;
    width: 100%;
    z-index: 1000;

    :deep(.p-menubar-start) {
        overflow-x: auto;
    }

    :deep(.p-menubar-end) {
        display: flex;
        gap: 0.5em;
    }
    height: var(--menu-bar-height);
}

// Keep header text styling the same in design & live; only the LIVE badge differs.
</style>

<style lang="scss">
:root {
    --release-banner-height: 36px;
}

body.release-viewing-active {
    .page-with-sidebar {
        #sidebar,
        .main-container {
            padding-top: calc(
                var(--menu-bar-height) + var(--release-banner-height)
            );
        }
    }
}

// Global styles for live mode - override PrimeVue CSS variables
// NOTE: Avoid global "force white" overrides here.
// They made the stek title/logo invisible when the menubar background wasn't actually in live mode.

// (Removed) live-mode gradient + forced colors to keep header consistent with design mode.

a#logo-route {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
    justify-content: center;
    padding-left: 8px;
    padding-right: 8px;
    text-decoration: none;
    text-decoration: inherit;
    visibility: visible !important;
    opacity: 1 !important;

    h1 {
        margin: 0;
        color: var(--text-color);
        padding: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        visibility: visible !important;
        opacity: 1 !important;

        .live-badge {
            font-size: 0.6em;
            font-weight: 700;
            padding: 0.2em 0.5em;
            border-radius: 4px;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: #d32f2f; /* red */
            border: 1px solid #d32f2f;
            background: transparent;
        }

        .release-badge {
            font-size: 0.6em;
            font-weight: 700;
            padding: 0.2em 0.5em;
            border-radius: 4px;
            letter-spacing: 0.05em;
            color: var(--stekz-primary-color, #842cff);
            border: 1px solid var(--stekz-primary-color, #842cff);
            background: transparent;
        }
    }

    #logo {
        display: block;
        clear: none;
        height: var(--text-xxl);
        max-width: 16em;
    }
}

// Keep logo/title colors consistent across modes; LIVE badge is handled above.

.page-with-sidebar {
    display: flex;
    flex-direction: row;

    // make room for the fixed menubar and presence bar on top
    #sidebar,
    .main-container {
        padding-top: var(--menu-bar-height);
        position: relative;
    }

    &.collapsed {
        #sidebar {
            z-index: 600;
            position: fixed;

            &:not(.hovering) {
                transform: translateX(-100%);
            }
        }
    }

    &.no-sidebar-mode {
        .main-container {
            padding-left: 0;
            margin-left: 0;
        }
    }

    #sidebar {
        height: 100vh;
        background: var(--p-menubar-background);
        border-right: 2px solid var(--surface-border, #dee2e6);
        display: flex;
        flex-direction: column;
        flex-grow: 0;
        flex-shrink: 0;
        left: 0;
        min-width: 120px;
        max-width: 500px;
        overflow-y: hidden; // note we need this for the default resize handle
        position: relative; /* Needed for absolute positioning of handle */
        transform: translateX(0);
        transition-property: transform;
        transition-timing-function: ease;
        transition-duration: 200ms;

        &.on-top {
            z-index: 600;
            overflow: visible;
        }
    }

    .main-container {
        width: 100%;
        height: 100vh;
        overflow-y: scroll;
        flex-grow: 1;
        display: flex;
        flex-direction: column;

        &.show-history-sidebar {
            flex-direction: row;
        }

        .history-sidebar {
            display: flex;
            flex: 0 1 20rem;
            border-left: 1px solid var(--palette-grey-300);
            flex-direction: column;
        }
    }
}

.page {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    padding: calc(var(--page-margin) * 0.5) var(--page-margin);
}

.resize-handle {
    position: absolute;
    top: 0;
    right: 0;
    width: 3px;
    height: 100%;
    background: transparent;
    cursor: col-resize;
    transition: 100ms 50ms ease-in-out;

    &:hover,
    &.resizing {
        background: var(--stekz-primary-background);
    }
}

.popover-container {
    .info-text {
        padding: 1rem;
        text-align: center;
    }

    :deep(.p-datatable) {
        .sync-action-buttons {
            display: flex;
            gap: 8px;
        }
    }
}

.toolbar-buttons-container {
    display: flex;
    gap: 0.5em;
    padding-top: 1rem;
    padding-bottom: 1rem;
}

/* Wrapper that holds RouterView inside the page transition — must fill available space */
.page-view-wrapper {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
}

/* Page enter transition — subtle fade + slide-up on every route change */
.page-enter-enter-active {
    transition:
        opacity 160ms ease-out,
        transform 160ms ease-out;
}
.page-enter-leave-active {
    transition: opacity 80ms ease-in;
}
.page-enter-enter-from {
    opacity: 0;
    transform: translateY(6px);
}
.page-enter-leave-to {
    opacity: 0;
}
</style>
